/**
 * Skip Service - Centralized skip logic for V2 controller
 * Handles all skip-related operations with separation of concerns
 */

import type { Dispatch } from '@reduxjs/toolkit';

import Logger from '../../../services/loggerService';
import type { CopilotState } from '../data-layer/types';
import {
  addMessage,
  addSkippedStep,
  addVisitedStep,
  removeMessagesBySubType,
  setActiveStep,
  setAddAccessInputField,
  updateNameToLabelMap,
} from '../data-store/copilotSlice';
import type RemoveAccessController from '../utils/removeAccessUtils';

interface SkipServiceConfig {
  dispatch: Dispatch;
  getState: () => unknown;
}

export default class SkipService {
  private dispatch: Dispatch;

  public removeAccessController?: RemoveAccessController;

  private getState: () => unknown;

  // Callbacks for navigation
  public showFinalConfirmation?: () => void;

  constructor(config: SkipServiceConfig) {
    this.dispatch = config.dispatch;
    this.getState = config.getState;
  }

  /**
   * Check if step is already skipped
   */
  isStepAlreadySkipped(stepName: string): boolean {
    const state = this.getState() as { copilot?: CopilotState };
    const skippedSteps = state.copilot?.navigation.skippedSteps || [];

    return skippedSteps.includes(stepName);
  }

  /**
   * Generic skip step logic
   */
  skipStep(stepName: string): void {
    if (this.isStepAlreadySkipped(stepName)) {
      return;
    }

    // Dispatch "Skip for Now" user message
    this.dispatch(
      addMessage({
        id: `skip-response-${Date.now()}`,
        type: 'user_response',
        subType: 'userResponseNo',
        text: 'Skip',
        timestamp: Date.now(),
      })
    );

    if (stepName !== 'RemoveAccess') {
      this.dispatch(addSkippedStep(stepName));
      this.dispatch(addVisitedStep(stepName));
    }
  }

  /**
   * Skip to final confirmation
   */
  skipToFinalConfirmation(): void {
    this.showFinalConfirmation?.();
  }

  /**
   * Skip AddAccess step
   */
  skipAddAccess(): void {
    this.dispatch(
      updateNameToLabelMap({ stepName: 'RemoveAccess', label: 'Remove Access' })
    );
    this.dispatch(addSkippedStep('AddAccess'));
    this.dispatch(addSkippedStep('RemoveAccess'));
    this.dispatch(setAddAccessInputField(true));
    this.dispatch(addVisitedStep('RemoveAccess'));
    // this.dispatch(setActiveStep('RemoveAccess'));
    this.showFinalConfirmation?.();
  }

  /**
   * Skip RemoveAccess step
   */
  skipFromRemoveAccess(entitlementIndex = 0): void {
    const state = this.getState() as {
      copilot?: {
        removeAccessEntitlementIndex: number;
        entitlementIteration?: {
          entitlements: unknown[];
        };
      };
    };

    const currentIndex = state.copilot?.removeAccessEntitlementIndex ?? 0;
    const totalEntitlements =
      state.copilot?.entitlementIteration?.entitlements || [];

    Logger.log(entitlementIndex);

    // Check if this is the last entitlement
    const isLastTemplate = currentIndex >= totalEntitlements.length - 1;

    if (isLastTemplate) {
      this.dispatch(addSkippedStep('RemoveAccess'));
      this.dispatch(addVisitedStep('RemoveAccess'));
      this.showFinalConfirmation?.();
    } else {
      // Continue to next entitlement
      this.removeAccessController?.showNextEntitlementForm(currentIndex + 1);
    }
  }

  /**
   * Generic skip handler with navigation
   */
  handleSkip(
    currentStep: string,
    nextStep: string,
    entitlementIndex?: number
  ): void {
    if (this.isStepAlreadySkipped(currentStep)) {
      return;
    }

    this.skipStep(currentStep);

    // Step-specific skip handling with proper navigation
    const skipHandlers: Record<string, () => void> = {
      CreateAccount: () => this.navigateToNextAccountStep('UpdateAccount'),
      UpdateAccount: () => this.navigateToNextAccountStep('EnableAccount'),
      EnableAccount: () => this.navigateToNextAccountStep('DisableAccount'),
      DisableAccount: () => this.navigateToNextAccountStep('RemoveAccount'),
      RemoveAccount: () => this.navigateToNextAccountStep('AddAccess'),
      AddAccess: () => this.skipAddAccess(),
      RemoveAccess: () => this.skipFromRemoveAccess(entitlementIndex || 0),
      default: () => this.skipToFinalConfirmation(),
    };

    const handler = skipHandlers[currentStep] || skipHandlers.default;

    handler();
  }

  /**
   * Navigate to next account step after skipping current one
   */
  private navigateToNextAccountStep(nextStepName: string): void {
    // Add transition message with V2-specific subType
    this.dispatch(
      addMessage({
        id: `skip-transition-${nextStepName.toLowerCase()}-${Date.now()}`,
        type: 'ai_response',
        subType: 'skipTransition',
        text: `Moving to ${nextStepName} step...`,
        timestamp: Date.now(),
        loader: true,
      })
    );

    // Set active step and show next step form after cleanup (V2 pattern)
    setTimeout(() => {
      // Remove transition message before showing form
      this.dispatch(removeMessagesBySubType('skipTransition'));

      // Set navigation state
      this.dispatch(setActiveStep(nextStepName));
      this.dispatch(addVisitedStep(nextStepName));

      // Show the next step form
      this.showNextStepForm(nextStepName);
    }, 2000);
  }

  /**
   * Show form for the next step
   */
  private showNextStepForm(stepName: string): void {
    // This would need to be implemented based on the step factory pattern
    // For now, we'll add a callback that the controller can set
    if (this.showStepForm) {
      this.showStepForm(stepName);
    }
  }

  // Callback for showing step forms (to be set by controller)
  public showStepForm?: (stepName: string) => void;
}
